import axios from 'axios';
import { goSSO } from '@/utils';
import auth from '@/utils/auth';
import { ignoreMessage } from '@/config/interceptors/whiteList';

// 获取时区
const timeZone = (function timeZone() {
  const dt = new Date();
  const negMinutes = Math.round(dt.getTimezoneOffset() / 15) * 15;
  const minutes = Math.abs(negMinutes);
  const hourOffset = Math.floor(minutes / 60);
  const minuteOffset = minutes % 60;
  return `${
    (negMinutes <= 0 ? 'GMT+' : 'GMT-') + (hourOffset < 10 ? `0${hourOffset}` : hourOffset)
  }:${minuteOffset < 10 ? `0${minuteOffset}` : minuteOffset}`;
})();

// bengin: axios

// 创建请求实例
const instance = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL_AUTHORITY,
  withCredentials: true,
  timeout: 60000, // 超时10分钟
});

// instance.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
// 设置时区
instance.defaults.headers.common['time-zone'] = timeZone;
instance.defaults.headers.common['ecarx-client-id'] = process.env.VUE_APP_CLIENT_ID;

/**
 * get请求
 * @param {string} url 地址
 * @param {object} params 参数
 * @param {object} config axios 额外配置
 * @returns {promise}
 */
const get = function get(url, params, config = {}) {
  return instance.get(url, { params, ...config });
};

/**
 * post请求
 * @param {string} url 地址
 * @param {object} params 参数
 * @param {object} config axios 额外配置
 * @returns {promise}
 */
const post = function post(url, params, config = {}) {
  return instance.post(url, params, config);
};

/**
 * delete 请求
 * @param {string} url 地址
 * @param {object} config axios 额外配置
 * @returns {promise}
 */
const deleteReq = function deleteReq(url, config = {}) {
  return instance.delete(url, config);
};

/**
 * put请求
 * @param {string} url 地址
 * @param {object} params 参数
 * @param {object} config axios 额外配置
 * @returns {promise}
 */
const putReq = function putReq(url, params, config = {}) {
  return instance.put(url, params, config);
};

/**
 * 并行任务
 * @param {object} tasks 并行任务数组
 */
const all = function all(tasks) {
  return axios.all(tasks);
};
// end: axios

// 计算字符串的hash值
function getHashCode(str, caseSensitive) {
  if (!caseSensitive) {
    str = str.toLowerCase();
  }
  let hash = 1315423911;
  let ch;
  for (let i = str.length - 1; i >= 0; i--) {
    ch = str.charCodeAt(i);
    hash ^= (hash << 5) + ch + (hash >> 2);
  }
  return hash & 0x7fffffff;
}
// 获取请求令牌
const getUniqueToken = () => {
  return axios
    .get(`${process.env.VUE_APP_API_BASE_URL_GATEWAY}/gateway/uniqueToken/get`, {
      withCredentials: true,
    })
    .then((res) => res.data.data || '')
    .catch(() => '');
};
// 储存请求token
const reqTokes = {};

/**
 * 拦截请求
 */
instance.interceptors.request.use(
  async (config) => {
    if (config.reqValid) {
      // 计算链接hash
      const urlHash = getHashCode(`${config.method}${config.url}`);
      const reqToken = reqTokes[urlHash] || (await getUniqueToken());
      if (reqToken) {
        reqTokes[urlHash] = reqToken;
        config.urlHash = urlHash;
        config.headers = {
          ...config.headers,
          req_token: reqToken,
          req_valid: 'YES_NEED',
        };
      }
    }

    config.st = +new Date();
    return config;
  },
  (err) => Promise.reject(err)
);

instance.interceptors.response.use(
  (response) => response,
  (err) => {
    const { message: msg, response, config } = err;
    const { url, method, baseURL, data: _data = {}, params: _params = {}, st = 0 } = config;
    const rt = +new Date() - st;
    const params = {
      url: url.replace(baseURL, ''),
      md: method,
      mg: msg.slice(0, 500),
      code: 'no',
      rt,
      cext: { data: _data, params: _params },
    };
    if (response) {
      params.code = response.status;
      // 根据原拦截错误信息，见下图
      if (params.mg) params.mg = (response.data.message || '').slice(0, 500);
    }
    window.tracker && window.tracker.sendError('xhr', params);
    return Promise.reject(err);
  }
);

/**
 * 拦截响应
 */
instance.interceptors.response.use(
  (res) => {
    // 自定义响应成功逻辑，全局拦截接口，根据不同业务做不同处理，响应成功监控等
    const { headers, data: resData, config } = res;
    if (config.urlHash) {
      // 删除记录的reqtoken
      delete reqTokes[config.urlHash];
      if ([200001, 200002].includes(resData.code)) {
        return Promise.reject(new Error(resData.message));
      }
    }

    if (headers.username) {
      auth.setUserName(headers.username);
    }
    // 存储clientid信息 单点登录
    if (headers.clientid) {
      auth.setClientId(headers.clientid);
    }
    // 服务器接口头部认证通过时，将 token 存储在客户端
    // TODO：历史问题，可能每个项目有差异，针对文件下载，如果是文件流，直接返回
    const allowContentType = [
      'application/octet-stream;charset=utf-8',
      'application/vnd.ms-excel;charset=utf-8',
      'application/pdf;charset=utf-8',
      'multipart/form-data',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8'
    ];
    if (allowContentType.includes(headers['content-type'])) {
      return resData;
    }
    // 此处 meta 是前后端约定
    if (resData.meta) {
      const { success, message: msg } = resData.meta;
      return { success, msg, ...resData.content };
    }
    const { success, message: msg } = resData;
    return { success, msg, ...resData.content };
  },
  (err) => {
    const { message: msg, response, config } = err;
    let errorMessage = msg;
    if (!response) {
      return Promise.reject(err);
    }
    if (config.urlHash) {
      // 删除记录的reqtoken
      delete reqTokes[config.urlHash];
      if ([200001, 200002].includes(response.data.code)) {
        window.antd.message.error(response.data.message);
        return Promise.reject(new Error(response.data.message));
      }
    }
    const { status, data, headers } = response;
    // 存储clientid信息 单点登录
    if (headers.clientid) {
      auth.setClientId(headers.clientid);
    }
    errorMessage = (data.meta && data.meta.message) || data.message || response.statusText;
    switch (status) {
      case 403:
        if (errorMessage === 'userInfoNeedComplete') {
          window.location.replace(
            `${process.env.VUE_APP_API_SSO_URL}/collect.html?page=userInfoNeedComplete&client=${
              process.env.VUE_APP_CLIENT_ID
            }&target=${encodeURIComponent(window.location.href)}`
          );
        } else {
          window.antd.message.error(errorMessage);
        }
        break;
      case 401:
        goSSO();
        break;
      case 404:
        window.antd.message.error(errorMessage);
        break;
      case 500:
        if (!ignoreMessage(config.url)) {
          window.antd.message.error('服务器异常，请联系系统管理员。');
        }
        break;
      default:
    }
    return Promise.reject(new Error(errorMessage));
  }
);

// 导出模块
export default {
  get,
  post,
  delete: deleteReq,
  put: putReq,
  all,
};
